import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigurationService } from '@app/core/config/configuration.service';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

/* === SonarQube Anmerkung ===
 * SonarQube möchte, dass wir codePointAt anstelle von charCodeAt verwenden.
 * Das funktioniert aber nicht für unseren Zweck, da wir Zahlen und keine Strings erwarten.
 * Da wir aber auf einem Base64-kodierten String arbeiten, spielt das Ganze auch für uns keine Rolle.
 */

@Injectable({providedIn: 'root'})
export class CryptoService {
  private readonly http = inject(HttpClient);
  private readonly configurationService = inject(ConfigurationService);

  private readonly apiBase = this.configurationService.getConfig().backendUrl;
  private key: CryptoKey | null = null;

  private async getKey(): Promise<CryptoKey> {
    if (this.key) {
      return this.key;
    }
    // Request private key from backend
    return firstValueFrom(
      this.http.get<{ key: string }>(`${this.apiBase}/login/cryptoKey`)
        .pipe(
          map((data: { key: string }) => data['key'])
      )).then( stringKey => {
        // Convert Base64 encoded key into CryptoKey
        let binaryKey = Uint8Array.from(atob(stringKey), c => c.charCodeAt(0)); // NOSONAR - Siehe Anmerkung oben
        let algorithm: AesKeyAlgorithm = {name: 'AES-GCM', length: 256};
        return crypto.subtle.importKey('raw', binaryKey, algorithm, false, ['encrypt', 'decrypt']);
      }).then(scKey => {
        // Store CryptoKey for later and return value
        this.key = scKey;
        return this.key;
      });
  }

  async encrypt(plaintext: string): Promise<string> {
    if (crypto.subtle) {
      // Generate random IV
      let scIv = crypto.getRandomValues(new Uint8Array(12));
      return this.getKey().then(scKey => {
        // Encrypt plaintext
        let algorithm: AesGcmParams = {name: 'AES-GCM', iv: scIv};
        let scPlaintext = new TextEncoder().encode(plaintext);
        return crypto.subtle.encrypt(algorithm, scKey, scPlaintext)
      }).then(scCiphertext => {
        // Convert results to Base64
        let ivString = btoa(String.fromCharCode(...new Uint8Array(scIv))); // NOSONAR - Siehe Anmerkung oben
        let ciphertextString = btoa(String.fromCharCode(...new Uint8Array(scCiphertext))); // NOSONAR - Siehe Anmerkung oben
        // Return IV and ciphertext
        return ivString + '.' + ciphertextString;
      });
    }
    throw new Error("Subtle-Crypto Bibliothek ist nicht verfügbar");
  }

  async decrypt(ciphertext: string): Promise<string> {
    // Split IV and ciphertext
    let parts = (ciphertext || '').split('.');
    if (parts.length == 2 && crypto.subtle) {
      let ivString = parts[0];
      let ciphertextString = parts[1];
      return this.getKey().then(scKey => {
        // Convert Base64 to byte array
        let scIv = Uint8Array.from(atob(ivString), c => c.charCodeAt(0)); // NOSONAR - Siehe Anmerkung oben
        let scCiphertext = Uint8Array.from(atob(ciphertextString), c => c.charCodeAt(0)); // NOSONAR - Siehe Anmerkung oben
        // Decrypt ciphertext
        let algorithm: AesGcmParams = {name: 'AES-GCM', iv: scIv};
        return crypto.subtle.decrypt(algorithm, scKey, scCiphertext)
      }).then(scPlaintext => {
        // Convert results to string
        return new TextDecoder().decode(scPlaintext);
      });
    }
    throw new Error("Subtle-Crypto Bibliothek ist nicht verfügbar");
  }
}
